import _ from 'lodash';
import { action, makeObservable } from 'mobx';
import * as Sentry from '@sentry/react';

import { webServiceProvider } from '../shared';
import AppStore from './App';
import ShopStore from './ShopStore';
import AbstractStore from './abstract/AbstractStore';

class ContactStore extends AbstractStore {
  contactAbortController = null;
  createContactAbortController = new AbortController();

  constructor() {
    super();
    makeObservable(this, {
      getShopContacts: action,
      createShopContact: action,
    });
  }

  getShopContacts = async (
    shopId = ShopStore.currentShop.id,
    { offset, limit, filter, sort } = {}
  ) => {
    if (!shopId || shopId === -1) {
      return [];
    }
    try {
      // Cancel any ongoing request
      if (this.contactAbortController) {
        this.contactAbortController.abort();
      }

      // Create new abort controller for this request
      this.contactAbortController = new AbortController();

      const { data } = await webServiceProvider.getMany(
        'v1/contact',
        {
          shopId,
        },
        this.contactAbortController.signal
      );

      // Return the data, ensuring it's always an array
      return data || [];
    } catch (e) {
      // Don't throw error if request was intentionally aborted
      if (e.name === 'AbortError') {
        // Return empty array instead of undefined for aborted requests
        return [];
      }

      // Send error to Sentry for monitoring
      Sentry.captureException(e, {
        tags: {
          component: 'ContactStore',
          method: 'getShopContacts',
          shopId: shopId,
        },
        extra: {
          shopId,
          offset,
          limit,
          filter,
          sort,
        },
      });

      // For network errors or other issues, throw to prevent caching
      // This ensures we don't cache empty results on error
      throw e;
    }
  };

  createShopContact = async (payload) => {
    // check duplicate contact - with same email for shop
    const contacts = await this.getShopContacts(payload.shopId);
    const duplicateContact = _.find(contacts, (contact) => {
      return contact.email === payload.email;
    });
    if (duplicateContact) {
      throw new Error('Contact with same email already exists');
    }

    this.createContactAbortController.abort();

    this.createContactAbortController = new AbortController();

    const data = await webServiceProvider.post(
      'v1/contact',
      payload,
      this.createContactAbortController.signal
    );

    return data;
  };

  updateShopContact = async (contactId, payload) => {
    try {
      const data = await webServiceProvider.put(
        `v1/contact/${contactId}`,
        payload
      );

      return data;
    } catch (e) {
      AppStore.addError(`Error in updating contact: ${e.message}`);
      throw e;
    }
  };

  deleteShopContact = async (contactId) => {
    try {
      const data = await webServiceProvider.delete(`v1/contact/${contactId}`);

      return data;
    } catch (e) {
      AppStore.addError(`Error in deleting contact: ${e.message}`);
      throw e;
    }
  };
}

const ContactStoreInstance = new ContactStore();
export default ContactStoreInstance;
